/* eslint-disable no-plusplus */

const QUALITY_THRESHOLDS = {
  edgeFocus: 20, // Valor mínimo para considerar buena nitidez
  contrast: 254, //  Valor mínimo
  brightness: 100, //   Valor máximo
  noise: 60, // Valor mínimo
  saturation: 40 // Valor mínimo
};

type ImageData = {
  width: number;
  height: number;
  data: Uint8ClampedArray;
};

const isImageAccepted = (edgeFocus: number) =>
  edgeFocus >= QUALITY_THRESHOLDS.edgeFocus;

/**
 * applyFilter
 * This function takes an ImageData object and a filter represented as a 2D array,
 * To calculate the focus of an image using the Laplacian filter.
 *
 * @param {Object} imageData The ImageData to apply the filter to.
 * @param { Array<number[]>} filter The filter to apply, represented as a 2D array of numbers.
 * @returns {ImageData} The filtered ImageData.
 */
const applyFilter = (
  imageData: ImageData,
  filter: Array<number[]>
): ImageData => {
  const { width, height, data } = imageData;
  const filteredData = new Uint8ClampedArray(data.length);

  const filterSize = filter.length;
  const filterOffset = Math.floor(filterSize / 2);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let r = 0;
      let g = 0;
      let b = 0;

      for (let fy = 0; fy < filterSize; fy++) {
        for (let fx = 0; fx < filterSize; fx++) {
          const ix = x + fx - filterOffset;
          const iy = y + fy - filterOffset;
          if (ix >= 0 && ix < width && iy >= 0 && iy < height) {
            const index = (iy * width + ix) * 4;
            const weight = filter[fy][fx];

            r += data[index] * weight;
            g += data[index + 1] * weight;
            b += data[index + 2] * weight;
          }
        }
      }

      const newIndex = (y * width + x) * 4;
      filteredData[newIndex] = Math.min(Math.max(r, 0), 255);
      filteredData[newIndex + 1] = Math.min(Math.max(g, 0), 255);
      filteredData[newIndex + 2] = Math.min(Math.max(b, 0), 255);
      filteredData[newIndex + 3] = data[newIndex + 3];
    }
  }

  return {
    width,
    height,
    data: filteredData
  };
};

/**
 * Calculates the variance of pixel values in the given ImageData.
 *
 * @param {Object} imageData - The ImageData object containing pixel data.
 * @returns {number} The variance of pixel values in the image.
 */
export const calculateVariance = (imageData: ImageData): number => {
  const { data } = imageData;
  const numPixels = data.length / 4;
  let mean = 0;

  for (let i = 0; i < data.length; i += 4) {
    mean += data[i];
  }
  mean /= numPixels;

  let variance = 0;
  for (let i = 0; i < data.length; i += 4) {
    variance += (data[i] - mean) ** 2;
  }
  variance /= numPixels;

  return variance;
};

/**
 * Calculates the edge focus of an image using Laplacian filter.
 *
 * @param {Object} imageData - The ImageData object representing the image.
 * @returns {number} The variance of the filtered image data, which indicates the edge focus.
 */
export const calculateEdgeFocus = (imageData: ImageData): number => {
  const laplacianFilter = [
    [0, -1, 0],
    [-1, 4, -1],
    [0, -1, 0]
  ];

  const filteredImageData = applyFilter(imageData, laplacianFilter);
  const variance = calculateVariance(filteredImageData);

  return variance;
};

/**
 * The function calculates the contrast of an image using the brightness values of its pixels.
 * @param {Object} imageData - The `imageData` parameter in the `calculateContrast` function is of
 * type `ImageData`, which represents the underlying pixel data of an HTML canvas element. It contains
 * information about the width, height, and pixel data of the image. The pixel data is stored in a
 * one-dimensional array where each
 * @returns {number} The function `calculateContrast` returns the difference between the maximum and minimum
 * brightness values calculated from the image data.
 */
export const calculateContrast = (imageData: ImageData): number => {
  const { data } = imageData;
  let min = 255;
  let max = 0;
  for (let i = 0; i < data.length; i += 4) {
    const brightness = (data[i] + data[i + 1] + data[i + 2]) / 3;
    min = Math.min(min, brightness);
    max = Math.max(max, brightness);
  }
  return max - min;
};

/**
 * calculateBrightness
 * Calculates the average brightness of an image based on its ImageData.
 *
 * @param {Object} imageData The ImageData object containing pixel data of the image.
 * @returns {number} The average brightness value of the image.
 */
export const calculateBrightness = (imageData: ImageData): number => {
  const { data } = imageData;
  let totalBrightness = 0;
  const numPixels = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    //    Convertir RGB a brillo usando la fórmula de luminancia
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const brightness = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    totalBrightness += brightness;
  }

  return totalBrightness / numPixels;
};

/**
 * calculateNoise
 * Calculates the noise level in an image based on the brightness of each pixel.
 * @param {Object} imageData - The ImageData object containing pixel data.
 * @returns {number} The calculated noise level of the image.
 */
export const calculateNoise = (imageData: ImageData): number => {
  const { data } = imageData;
  const numPixels = data.length / 4;
  let meanBrightness = 0;

  for (let i = 0; i < data.length; i += 4) {
    meanBrightness += data[i];
  }
  meanBrightness /= numPixels;

  let noise = 0;
  for (let i = 0; i < data.length; i += 4) {
    noise += (data[i] - meanBrightness) ** 2;
  }
  noise /= numPixels;

  return Math.sqrt(noise);
};

/**
 * calculateSaturation
 * Calculates the average saturation of an image.
 *
 * @param {Object} imageData - The ImageData object containing pixel data.
 * @returns {number} The average saturation value of the image.
 */
export const calculateSaturation = (imageData: ImageData): number => {
  const { data } = imageData;
  let saturationSum = 0;
  const numPixels = data.length / 4;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const saturation = max - min;
    saturationSum += saturation;
  }

  return saturationSum / numPixels;
};

/**
 * handleImageQualityThresholds
 * Handles image quality thresholds based on edge focus calculation.
 *
 * @param {string} rawBase64 - The base64 string of the image to be processed.
 * @param {Function} callback - A function that receives a boolean indicating if the image meets the quality threshold.
 */
export const handleImageQualityThresholds = (
  rawBase64: string,
  callback: (isAccepted: boolean) => void
) => {
  if (rawBase64) {
    const img = new Image();
    img.onload = () => {
      // Crea un canvas y dibuja la imagen en él
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      // Extrae los datos de la imagen del canvas
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const edgeFocus = calculateEdgeFocus(imageData);
      const isAccepted = isImageAccepted(edgeFocus);
      callback(isAccepted);
      return isAccepted;
    };
    img.src = rawBase64;
  }
};
